"use client";

import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Edit3, Save, X } from "lucide-react";
import QuillEditor from "@/components/product/QuillEditor";

interface LegalDocument {
  id: string;
  title: string;
  description: string;
  content: string;
}

const initialLegalDocuments: LegalDocument[] = [
  {
    id: "terms-conditions",
    title: "Terms And Conditions",
    description: "Define the terms and conditions for using your services",
    content: "",
  },
  {
    id: "privacy-policy", 
    title: "Privacy Policy",
    description: "Explain how you collect, use, and protect user data",
    content: "",
  },
  {
    id: "return-refund",
    title: "Return and Refund Policy", 
    description: "Define your return and refund policies for customers",
    content: "",
  },
  {
    id: "disclaimer",
    title: "Disclaimer",
    description: "Legal disclaimer for your products and services",
    content: "",
  },
  {
    id: "cookie-policy",
    title: "Cookie Policy",
    description: "Explain how your website uses cookies",
    content: "",
  },
  {
    id: "copyright-notice",
    title: "Copyright Notice", 
    description: "Copyright information and intellectual property rights",
    content: "",
  },
  {
    id: "eula",
    title: "End User License Agreement",
    description: "Software license terms for end users",
    content: "",
  },
];

const LegalPages = () => {
  const [documents, setDocuments] = useState<LegalDocument[]>(initialLegalDocuments);
  const [editingDocument, setEditingDocument] = useState<string | null>(null);
  const [editContent, setEditContent] = useState<string>("");

  const handleEdit = (documentId: string) => {
    const document = documents.find(doc => doc.id === documentId);
    if (document) {
      setEditContent(document.content);
      setEditingDocument(documentId);
    }
  };

  const handleSave = (documentId: string) => {
    setDocuments(prev => 
      prev.map(doc => 
        doc.id === documentId 
          ? { ...doc, content: editContent }
          : doc
      )
    );
    setEditingDocument(null);
    setEditContent("");
  };

  const handleCancel = () => {
    setEditingDocument(null);
    setEditContent("");
  };

  return (
    <div className="space-y-2">
      {documents.map((document) => (
        <Card key={document.id}>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>{document.title}</CardTitle>
              <CardDescription>{document.description}</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {editingDocument === document.id ? (
                <>
                  <Button
                    onClick={() => handleSave(document.id)}
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    Save
                  </Button>
                  <Button
                    onClick={handleCancel}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => handleEdit(document.id)}
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <Edit3 className="w-4 h-4" />
                  Edit
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {editingDocument === document.id ? (
              <QuillEditor
                initialValue={editContent}
                onChange={setEditContent}
                placeholder={`Enter your ${document.title.toLowerCase()}...`}
                height={200}
              />
            ) : (
              <div className="min-h-[100px] p-4 bg-muted rounded-lg">
                {document.content ? (
                  <div 
                    className="prose prose-sm max-w-none dark:prose-invert"
                    dangerouslySetInnerHTML={{ __html: document.content }}
                  />
                ) : (
                  <p className="text-muted-foreground italic">
                    No content added yet. Click Edit to add {document.title.toLowerCase()}.
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default LegalPages;